import { catchError, finalize, switchMap } from "rxjs/operators";
import { inject } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaderResponse,
  HttpInterceptorFn,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent,
} from "@angular/common/http";
import { Router } from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { AuthToken } from "@company-box/models";
import { AUTH_SVC, AuthService } from "@company-box/services";
import { BehaviorSubject, from, Observable, throwError } from "rxjs";

let isRefreshingToken = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const refreshTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  // Capture injected deps at the top so we don't rely on the injection context
  // surviving into nested helpers called from rxjs operators.
  const router = inject(Router);
  const authService = inject(AUTH_SVC) as AuthService;
  const logger = inject(NGXLogger);

  return next(req).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        switch ((error as HttpErrorResponse).status) {
          case 500:
            return handle500Error(req, next, error, router, authService, logger);
          case 400:
            return handle400Error(error, router, authService);
          case 401:
            // TEMP diagnostic — remove once invalid_token detection is verified.
            // eslint-disable-next-line no-console
            console.warn("[refreshTokenInterceptor] 401 received", {
              url: req.url,
              body: error.error,
              wwwAuthenticate:
                error.headers?.get?.("WWW-Authenticate") ?? error.headers?.get?.("www-authenticate"),
            });
            return handle401Error(req, next, error, router, authService, logger);
          default:
            return throwError(() => error);
        }
      } else {
        return throwError(() => error);
      }
    })
  );
};

/*
 * TODO: Ugly hack for getting around oauth error from secondary service, call to ProjectService calls
 * UserService to validate token and gets "invalid_token" response
 * This should be handled in ProjectService as a OAuth2 client but it seems to be complicated to get it working
 */
const handle500Error = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  error: HttpErrorResponse,
  router: Router,
  authService: AuthService,
  logger: NGXLogger
): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> => {
  if (error.error.message.trim() === "400") {
    return handle401Error(req, next, error, router, authService, logger);
  }
  return throwError(() => error);
};

const handle400Error = (
  error: HttpErrorResponse,
  router: Router,
  authService: AuthService
): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> => {
  if (
    error &&
    error.status === 400 &&
    error.error &&
    (error.error.error === "invalid_grant" || error.error.error === "invalid_token")
  ) {
    // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
    authService.clearToken();

    // Route to the login page (implementation up to you)
    router.navigate(["/login"]);
  }
  return throwError(() => error);
};

const handle401Error = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  error: HttpErrorResponse,
  router: Router,
  authService: AuthService,
  logger: NGXLogger
): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> => {
  const token = authService.getToken();

  // A 401 with `invalid_token` means the access token is no longer accepted.
  // Skip the refresh attempt and force a fresh login.
  if (isInvalidTokenError(error)) {
    // eslint-disable-next-line no-console
    console.warn("[refreshTokenInterceptor] invalid_token detected, forcing logout + /login redirect");
    logger.error("401 invalid_token, forcing login");
    refreshTokenSubject.next(null);
    logoutUser(router, authService, logger);
    return throwError(() => error);
  }

  if (!token) {
    logger.error("401 not refreshing token");
    // If there is no token and we get a 401 force new login
    refreshTokenSubject.next(null);
    logoutUser(router, authService, logger);
    return throwError(() => new Error("401 not refreshing token"));
  } else if (!isRefreshingToken) {
    isRefreshingToken = true;

    // Reset here so that the following requests wait until the token
    // comes back from the refreshToken call.
    refreshTokenSubject.next(null);

    return from(authService.renewToken()).pipe(
      switchMap((newToken: AuthToken) => {
        if (newToken && newToken.accessToken) {
          logger.info("refreshed token");
          refreshTokenSubject.next(newToken);
          return next(req);
        }

        logger.error("error refreshing token: any");
        // If we don't get a new token, we are in trouble so logout.
        logoutUser(router, authService, logger);
        const err = new Error("error refreshing token: any");
        return throwError(() => err);
      }),
      catchError((error: any) => {
        // If there is an exception calling 'refreshToken', bad news so logout.
        logger.error("error refreshing token", error);
        logoutUser(router, authService, logger);
        return throwError(() => error);
      }),
      finalize(() => {
        isRefreshingToken = false;
      })
    );
  }
  return next(req);
};

// OAuth2 surfaces `invalid_token` in different places depending on the server
// (JSON body, raw text body, WWW-Authenticate header, nested error fields, etc.)
// and CORS can hide the WWW-Authenticate header on cross-origin calls.
// Check the body and headers exhaustively for the marker.
const isInvalidTokenError = (error: HttpErrorResponse): boolean => {
  const body = error?.error;
  if (body != null) {
    if (typeof body === "string" && body.includes("invalid_token")) return true;
    if (typeof body === "object") {
      try {
        if (JSON.stringify(body).includes("invalid_token")) return true;
      } catch {
        // ignore stringify failures (e.g. circular refs) and fall through
      }
    }
  }

  const wwwAuthenticate = error?.headers?.get?.("WWW-Authenticate") ?? error?.headers?.get?.("www-authenticate");
  if (wwwAuthenticate && wwwAuthenticate.includes("invalid_token")) return true;

  return false;
};

const logoutUser = (router: Router, authService: AuthService, logger: NGXLogger) => {
  logger.trace("clear token and request login");
  authService.clearToken();

  // eslint-disable-next-line no-console
  console.warn("[refreshTokenInterceptor] navigating to /login");
  router
    .navigate(["/login"])
    .then((ok) => {
      // eslint-disable-next-line no-console
      console.warn("[refreshTokenInterceptor] navigation result:", ok);
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error("[refreshTokenInterceptor] navigation failed:", err);
    });
};
